import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, FileText, Copy, Download, Check, Calendar, Tag } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getServiceScript } from "@/Api/serviceScripts";
import { Badge } from "@/components/ui/badge";
import { ServiceScript } from "@/types/serviceScript";

interface ShowServiceScriptProps {
  uuid: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ShowServiceScript({
  uuid,
  isOpen,
  onClose,
}: ShowServiceScriptProps) {
  const [script, setScript] = useState<ServiceScript | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!isOpen || !uuid) return;

      setIsLoading(true);
      try {
        const response = await getServiceScript(uuid);
        if (response.data?.response) {
          setScript(response.data.response);
        }
      } catch (error) {
        console.error("Erro ao carregar script:", error);
        toast.error("Erro ao carregar script");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isOpen, uuid]);

  const handleCopy = async () => {
    if (!script?.content) return;

    // Converte HTML para texto preservando quebras de linha
    let text = script.content;

    // Substitui tags de quebra de linha por \n
    text = text.replace(/<br\s*\/?>/gi, "\n");
    text = text.replace(/<\/p>/gi, "\n");
    text = text.replace(/<\/div>/gi, "\n");
    text = text.replace(/<\/li>/gi, "\n");

    // Remove todas as outras tags HTML
    text = text.replace(/<[^>]+>/g, "");

    // Decodifica entidades HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    text = tempDiv.textContent || tempDiv.innerText || "";

    // Remove espaços extras no início/fim de cada linha e linhas vazias consecutivas
    text = text
      .split("\n")
      .map(line => line.trim())
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Texto copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Erro ao copiar texto");
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setScript(null);
      setCopied(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            {isLoading ? "Carregando..." : script?.title}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : script ? (
          <div className="space-y-5">
            {/* Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(script.created_at)}
              </div>
              <div className="flex items-center gap-1.5">
                {script.contest ? (
                  <>
                    <span
                      className="px-2 py-0.5 text-xs rounded-full text-center font-medium border-2 bg-white"
                      style={{
                        borderColor: script.contest.lottery?.color || '#023859',
                        color: script.contest.lottery?.color || '#023859'
                      }}
                    >
                      {script.contest.name}
                    </span>
                    {script.contest.lottery && (
                      <span
                        className="px-1.5 py-0.5 text-[10px] rounded-full text-white text-center"
                        style={{ backgroundColor: script.contest.lottery.color }}
                      >
                        {script.contest.lottery.name}
                      </span>
                    )}
                  </>
                ) : (
                  <Badge variant="secondary" className="text-xs">Atemporal</Badge>
                )}
              </div>
              <Badge variant={script.is_active ? "default" : "destructive"}>
                {script.is_active ? "Ativo" : "Inativo"}
              </Badge>
            </div>

            {/* Tags */}
            {script.tags && script.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                {script.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2 py-0.5 text-xs rounded-full text-white"
                    style={{ backgroundColor: tag.color || "#6b7280" }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}

            {/* Conteúdo com botão de copiar */}
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Conteúdo</span>
                <Button
                  onClick={handleCopy}
                  size="sm"
                  variant={copied ? "default" : "outline"}
                  className="h-7 gap-1"
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      Copiar
                    </>
                  )}
                </Button>
              </div>
              <div
                className="bg-muted rounded-lg p-4 prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: script.content }}
              />
            </div>

            {/* Arquivos */}
            {script.attachments && script.attachments.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Arquivos ({script.attachments.length})
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {script.attachments.map((attachment) => (
                    <a
                      key={attachment.id}
                      href={attachment.file_path || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                    >
                      <Download className="h-4 w-4 text-primary" />
                      <span className="text-sm truncate">{attachment.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Criado por */}
            {script.created_by && (
              <div className="text-sm text-muted-foreground">
                Criado por: {script.created_by.name}
              </div>
            )}

            {/* Botão grande de copiar */}
            <Button onClick={handleCopy} className="w-full" size="lg">
              {copied ? (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Texto Copiado!
                </>
              ) : (
                <>
                  <Copy className="h-5 w-5 mr-2" />
                  Copiar Texto do Script
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Script não encontrado
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
